import type { QueryStatus } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import { useCompanyBaseInfoQuery } from "app/pages/sales-intelligence/modules/company-analysis/queries/useCompanyBaseInfoQuery";
import type { CompanyBaseInfo } from "app/pages/sales-intelligence/modules/company-analysis/types/company";
import type { LeadsSearchRow } from "app/pages/sales-intelligence/modules/leads-search";
import { leadRowTypeGuard } from "app/pages/sales-intelligence/modules/leads-search";
import { getLeadDomainFromRow } from "app/pages/sales-intelligence/modules/leads-search/utils/rows";
import signalEventsService from "app/pages/sales-intelligence/sub-modules/account-overview/company/services/signals/signalEventsService";
import type { SignalEventDto } from "app/pages/sales-intelligence/sub-modules/account-overview/company/types/signals";
import useSignalsEventsForTooltipQuery from "app/pages/sales-intelligence/sub-modules/account-overview/shared/query/queries/useSignalsEventsForTooltipQuery";
import { accountOverviewQueryKeys } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/query/queryKeyFactory";
import type { SignalsDescription } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsDescription";
import type SignalsGroupedByType from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsGroupedByType";
import type { SignalTimeframeDTO } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalTimeframeDTO";
import {
    selectAppliedSignalsMemoized,
    selectSignalsById,
    selectSignalsDescription,
    selectSignalsGroupedByType,
} from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

interface IUseSignalTooltipData<T> {
    row: T;
    isEnabled: boolean;
}

interface IUseSignalTooltipDataResponse {
    signalEvents?: SignalEventDto[];
    companyInfo?: Readonly<CompanyBaseInfo>;
    signalsDescription: Readonly<SignalsDescription> | null;
    signalsGroupedByType: Readonly<SignalsGroupedByType>;
    fetchStatus: QueryStatus;
}

export const useSignalTooltipData = <T extends LeadsSearchRow>(
    props: IUseSignalTooltipData<T>,
): IUseSignalTooltipDataResponse => {
    const appliedSignals = useSelector(selectAppliedSignalsMemoized);
    const signalsById = useSelector(selectSignalsById);
    const signalsGroupedByType = useSelector(selectSignalsGroupedByType);
    const signalsDescription = useSelector(selectSignalsDescription);
    const row = props.row;

    const queryClient = useQueryClient();

    const leadDomain = getLeadDomainFromRow(row);

    const isCompanyRow = leadRowTypeGuard.isCompanyLeadRow(row);

    const getEventsTimeframe = (): SignalTimeframeDTO => {
        return signalEventsService.getSignalEventsTimeFrame(appliedSignals[0]);
    };

    useDidUpdate(() => {
        void queryClient.invalidateQueries({
            queryKey: accountOverviewQueryKeys.companySignalsEventsForTooltip(leadDomain),
        });
    }, [signalsById]);

    const signalsEventsQuery = useSignalsEventsForTooltipQuery(
        {
            domain: leadDomain,
            isOther: false,
            includeRegionalDomains: isCompanyRow,
            ...getEventsTimeframe(),
        },
        {
            isEnabled: props.isEnabled,
        },
    );
    const shouldFetchCompanyInfo = (signalsEventsQuery.data ?? []).length > 0;

    const companyInfoQuery = useCompanyBaseInfoQuery(leadDomain, shouldFetchCompanyInfo);

    const dataFetchStatus = signalsEventsQuery.isFetched ? companyInfoQuery.status : signalsEventsQuery.status;

    return {
        fetchStatus: dataFetchStatus,
        companyInfo: companyInfoQuery.data,
        signalEvents: signalsEventsQuery.data,
        signalsDescription: signalsDescription,
        signalsGroupedByType: signalsGroupedByType,
    };
};
